import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

import SectionContainer from '../components/SectionContainer';
import PageHeader from '../components/PageHeader';
import { Project } from '../types';
import { getPublishedProjects } from '../services/projectService';

// Projects Hero
const ProjectsHero = styled.div`
  padding-top: 150px;
  padding-bottom: var(--spacing-lg);
`;

// Category Section
const CategorySection = styled.div`
  margin-bottom: var(--spacing-xl);
`;

const CategoryTitle = styled.h2`
  font-size: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: var(--spacing-md);
  font-weight: var(--font-weight-light);
  position: relative;
  display: inline-block;
  padding-bottom: var(--spacing-xs);
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 1px;
    background-color: var(--color-text);
  }
`;

const CategoryDescription = styled.p`
  font-size: 0.85rem;
  margin-bottom: var(--spacing-md);
  max-width: 900px;
  white-space: pre-line;
`;

// Projects Grid
const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled(Link)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  margin-bottom: var(--spacing-md);
`;

const ProjectCardImage = styled.img`
  width: 100%;
  aspect-ratio: 1/1;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.03);
  }
`;

const ProjectCardInfo = styled.div`
  padding: var(--spacing-sm) 0;
  margin-top: var(--spacing-xs);
`;

const ProjectCardTitle = styled.h3`
  margin-bottom: var(--spacing-xs);
  font-weight: normal;
  text-align: center;
  font-size: 1.3rem;
`;

const ProjectCardCategory = styled.p`
  text-transform: uppercase;
  font-size: 0.8rem;
  letter-spacing: 1px;
  margin-top: var(--spacing-xs);
  text-align: center;
  opacity: 0.7;
`;

// Interface pour regrouper les projets par catégorie
interface ProjectsByCategory {
  [category: string]: Project[];
}

// Descriptions des catégories
const categoryDescriptions: Record<string, string> = {
  'résidentiel': 'Nos projets d\'aménagement intérieur pour des espaces de vie privés, alliant fonctionnalité et élégance.',
  'restaurant': 'Conception d\'espaces de restauration élégants et fonctionnels, créant une ambiance unique pour une expérience mémorable.',
  'événementiel': 'L\'Agence Intérieurs réalise des décors éphémères pour des conférences de presse, des évènements de relations publiques, des catalogues ou shooting Presse… Nous avons aussi collaboré pendant les 10 ans à l’émission D&Co sur M6 en tant que Chef Décoration.\n\nRéférences : SHARP, LG, THE VOICE, LEADER PRICE, CASINO, Foire aux vins de FRANPRIX, cuisines ESPALUX…',
  'commercial': 'Aménagement d\'espaces commerciaux et professionnels optimisés pour l\'expérience client et l\'efficacité opérationnelle.',
  'hôtellerie': 'Design d\'intérieur pour établissements hôteliers, alliant confort, élégance et fonctionnalité.',
  'bureau': 'Nos réalisations pour des bureaux chaleureux et confortables.',
  'hôpital': 'L\'agence Interieurs réalise des aménagements pour des services hospitaliers et des maisons de santé.\n\nRéférences : hôpital de Poissy, Bordeaux, Lyon, Grenoble, Dunkerque, Necker, maison G. Lemarchal.',
};

// Helper to pluralize category names
const pluralizeCategory = (cat: string): string => {
  const lower = cat.toLowerCase();
  if (lower === 'bureau' || lower === 'bureaux') return 'Bureaux';
  if (lower === 'restaurant' || lower === 'restaurants') return 'Restaurants';
  if (lower === 'résidentiel' || lower === 'residentiel' || lower === 'résidentiels') return 'Résidentiel';
  if (lower === 'hôpital' || lower === 'hopital' || lower === 'hôpitaux') return 'Hôpitaux';
  if (lower === 'événementiel' || lower === 'evenementiel') return 'Événementiel';
  return cat.charAt(0).toUpperCase() + cat.slice(1);
};

const GalleryPage: React.FC = () => {
  const [, setProjects] = useState<Project[]>([]);
  const [projectsByCategory, setProjectsByCategory] = useState<ProjectsByCategory>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        const data = await getPublishedProjects();
        setProjects(data);
        
        // Regrouper les projets par catégorie
        const groupedProjects: ProjectsByCategory = {};
        data.forEach(project => {
          if (!groupedProjects[project.category]) {
            groupedProjects[project.category] = [];
          }
          groupedProjects[project.category].push(project);
        });
        
        setProjectsByCategory(groupedProjects);
        setError(null);
      } catch (err) {
        console.error('Erreur lors du chargement de la galerie:', err);
        setError('Impossible de charger la galerie. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };
    
    loadProjects();
  }, []);
  
  // Définir l'ordre spécifique des catégories
  const categoryOrder = ['résidentiel', 'bureau', 'restaurant'];
  
  // Fonction pour trier les catégories selon l'ordre spécifié
  const sortCategories = (categories: string[]): string[] => {
    if (!categories || categories.length === 0) return [];
    
    // Normaliser les catégories pour la comparaison
    const normalizeCategory = (cat: string): string => {
      const lowerCat = cat.toLowerCase();
      if (lowerCat === 'residentiel' || lowerCat === 'résidentiel' || lowerCat === 'résidentiels' || lowerCat === 'residentiels') return 'résidentiel';
      if (lowerCat === 'bureau' || lowerCat === 'bureaux') return 'bureau';
      if (lowerCat === 'restaurant' || lowerCat === 'restaurants') return 'restaurant';
      return lowerCat;
    };
    
    // Créer un tableau de catégories normalisées pour la comparaison
    const normalizedCategories = categories.map(cat => ({
      original: cat,
      normalized: normalizeCategory(cat)
    }));
    
    // Trier les catégories selon l'ordre spécifié
    const result: string[] = [];
    
    // D'abord ajouter les catégories dans l'ordre spécifié
    categoryOrder.forEach(orderCat => {
      const found = normalizedCategories.find(c => c.normalized === orderCat);
      if (found) {
        result.push(found.original);
      }
    });
    
    // Ensuite ajouter les catégories restantes
    normalizedCategories.forEach(cat => {
      if (!categoryOrder.includes(cat.normalized) && !result.includes(cat.original)) {
        result.push(cat.original);
      }
    });
    
    return result;
  };
  
  return (
    <div>
      <ProjectsHero>
        <SectionContainer>
          <PageHeader title="NOTRE GALERIE" />
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
              Chargement de la galerie...
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '50px 0', color: '#e74c3c' }}>
              {error}
            </div>
          ) : (
            sortCategories(Object.keys(projectsByCategory)).map(category => (
              <CategorySection key={category}>
                <CategoryTitle>{pluralizeCategory(category)}</CategoryTitle>
                <CategoryDescription>
                  {categoryDescriptions[category.toLowerCase()] || 
                   `Nos réalisations en galerie dans la catégorie ${category}.`}
                </CategoryDescription>
                
                <ProjectsGrid>
                  {projectsByCategory[category].map(project => (
                    <ProjectCard key={project.id} to={`/galerie/${project.id}`}>
                      <ProjectCardImage src={project.mainImage} alt={project.title} />
                      <ProjectCardInfo>
                        <ProjectCardTitle>{project.title}</ProjectCardTitle>
                        <ProjectCardCategory>
                        {pluralizeCategory(project.category)}{project.subcategory ? `, ${project.subcategory}` : ''}
                      </ProjectCardCategory>
                      </ProjectCardInfo>
                    </ProjectCard>
                  ))}
                </ProjectsGrid>
              </CategorySection>
            ))
          )}
        </SectionContainer>
      </ProjectsHero>
    </div>
  );
};

export default GalleryPage;
